import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, TrendingUp, DollarSign, Calendar, PiggyBank, ArrowRightLeft, Download, MessageCircle, FileText, Phone, GraduationCap, Plus, Trash2, BookOpen, Target } from 'lucide-react';
import { calculateSIP } from '../utils/mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ValueInput from './ui/value-input';
import MethodologyInfo from './ui/methodology-info';
import RiskDisclaimer from './RiskDisclaimer';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { RUBICON_LOGO_BASE64 } from '../utils/pdfAssets';

// Logo for PDF - imported from centralized assets to prevent string corruption

const InvestmentCalculators = () => {
  const navigate = useNavigate();
  // SIP Calculator State
  const [sipMonthly, setSipMonthly] = useState(5000);
  const [sipRate, setSipRate] = useState(12);
  const [sipYears, setSipYears] = useState(10);

  // Retirement Calculator State
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [monthlyExpenses, setMonthlyExpenses] = useState(50000);
  const [inflationRate, setInflationRate] = useState(6);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [lifeExpectancy, setLifeExpectancy] = useState(80);

  // STP Calculator State
  const [stpInitialAmount, setStpInitialAmount] = useState(1000000);
  const [stpTransferAmount, setStpTransferAmount] = useState(10000);
  const [stpDuration, setStpDuration] = useState(12);
  const [stpDebtReturn, setStpDebtReturn] = useState(7);
  const [stpEquityReturn, setStpEquityReturn] = useState(12);

  // SWP Calculator State
  const [swpInitialAmount, setSwpInitialAmount] = useState(1000000);
  const [swpMonthlyWithdrawal, setSwpMonthlyWithdrawal] = useState(10000);
  const [swpDuration, setSwpDuration] = useState(120); // months
  const [swpExpectedReturn, setSwpExpectedReturn] = useState(10);
  const [swpInflation, setSwpInflation] = useState(6);

  // Arabpati Calculator State
  const [arabpatiCurrentAge, setArabpatiCurrentAge] = useState(0);
  const [arabpatiTargetAge, setArabpatiTargetAge] = useState(25);
  const [arabpatiMonthlySIP, setArabpatiMonthlySIP] = useState(0);
  const [arabpatiExpectedReturn, setArabpatiExpectedReturn] = useState(12);
  const [arabpatiInitialAmount, setArabpatiInitialAmount] = useState(0);

  // Child Education Calculator State
  const EDUCATION_PRESETS = {
    engineering: { label: 'Engineering (India)', cost: 2000000 },
    medical: { label: 'Medical (India)', cost: 6000000 },
    mba_india: { label: 'MBA (India)', cost: 3000000 },
    study_abroad: { label: 'Study Abroad', cost: 10000000 },
    others: { label: 'Others (Custom)', cost: null }
  };
  const [childInflation, setChildInflation] = useState(10);
  const [childExpectedReturn, setChildExpectedReturn] = useState(12);
  const [children, setChildren] = useState([
    { id: 1, name: 'Child 1', currentAge: 2, educationAge: 18, educationType: 'engineering', customCost: 2000000 }
  ]);

  // Goal Planner State
  const GOAL_PRESETS = {
    car: { label: 'Buy a Car', icon: '🚗', cost: 1000000 },
    house: { label: 'Buy a House', icon: '🏠', cost: 8000000 },
    wedding: { label: 'Wedding', icon: '💍', cost: 2000000 },
    vacation: { label: 'Dream Vacation', icon: '✈️', cost: 500000 },
    business: { label: 'Start a Business', icon: '💼', cost: 2500000 },
    emergency: { label: 'Emergency Fund', icon: '🛡️', cost: 1000000 },
    others: { label: 'Custom Goal', icon: '⭐', cost: null }
  };
  const [goalType, setGoalType] = useState('car');
  const [goalName, setGoalName] = useState('My Dream Car');
  const [goalCostToday, setGoalCostToday] = useState(1000000);
  const [goalYears, setGoalYears] = useState(5);
  const [goalInflation, setGoalInflation] = useState(6);
  const [goalReturn, setGoalReturn] = useState(12);
  const [goalExistingSavings, setGoalExistingSavings] = useState(0);

  const addChild = () => {
    const nextId = (children[children.length - 1]?.id || 0) + 1;
    setChildren([
      ...children,
      { id: nextId, name: `Child ${children.length + 1}`, currentAge: 2, educationAge: 18, educationType: 'engineering', customCost: 2000000 }
    ]);
  };
  const removeChild = (id) => {
    if (children.length === 1) return;
    setChildren(children.filter(c => c.id !== id));
  };
  const updateChild = (id, field, value) => {
    setChildren(children.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format currency for PDF (using Rs. for compatibility with jsPDF)
  const formatCurrencyForPDF = (amount) => {
    // Handle undefined/null values
    if (amount === undefined || amount === null || isNaN(amount)) {
      return 'Rs. 0';
    }
    const formatted = new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0,
    }).format(amount);
    return `Rs. ${formatted}`;
  };

  // Common PDF header and footer functions
  const addPDFHeader = (doc, title) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 15;

    // Add logo
    try {
      const logoWidth = 50;
      const logoHeight = 48;
      doc.addImage(RUBICON_LOGO_BASE64, 'JPEG', (pageWidth - logoWidth) / 2, yPos, logoWidth, logoHeight);
      yPos += 52;
    } catch (error) {
      console.error('Error adding logo:', error);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      doc.setTextColor(0, 51, 102);
      doc.text('RUBICON FINSERVICES', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
    }

    // Title
    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    doc.text(title, pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('AMFI Registered Mutual Fund Distributor', pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
    doc.setFontSize(8);
    doc.text('ARN: 102867 | Initial Registration: 09-June-2015 | Valid till: 01-07-2027', pageWidth / 2, yPos, { align: 'center' });
    yPos += 3.5;
    doc.text('SIF: Valid till 15-03-2029  |  APRN: Valid till 17-10-2027  |  APMI: APMI-APRN01145', pageWidth / 2, yPos, { align: 'center' });
    yPos += 3;

    doc.setLineWidth(0.5);
    doc.setDrawColor(0, 51, 102);
    doc.line(14, yPos, pageWidth - 14, yPos);
    yPos += 10;

    return yPos;
  };

  const addPDFFooter = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerY = pageHeight - 25;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    
    doc.text('6A Dr.Rajendra Prasad Sarani, 4th Floor, R.No-20, Kolkata-700001', pageWidth / 2, footerY, { align: 'center' });
    doc.text('Email: communications@rubiconfinservices.com | Website: www.rubiconfinservices.com | Tel: +91-33-40442577', pageWidth / 2, footerY + 4, { align: 'center' });
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.text('"If you think about the long term, you can take good life decisions which you won\'t regret later"', pageWidth / 2, footerY + 9, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
  };

  // Handles either downloading or sharing the PDF via WhatsApp
  // On mobile browsers supporting Web Share API with files → shares the PDF directly
  // Otherwise → opens WhatsApp with pre-filled summary text AND downloads the PDF
  const handlePdfAction = async (doc, filename, summary, action) => {
    if (action === 'share') {
      const pdfBlob = doc.output('blob');
      const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });

      // Prefer native share with file (mobile: Android Chrome / iOS Safari 15+)
      if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        try {
          await navigator.share({
            files: [pdfFile],
            title: 'Rubicon Finservices - Calculator Report',
            text: summary,
          });
          return;
        } catch (err) {
          if (err?.name === 'AbortError') return; // user cancelled
          // fall through to desktop fallback
        }
      }

      // Desktop/unsupported fallback:
      // 1) Open WhatsApp Web FIRST (must stay in user gesture context, not after await)
      // 2) Then download the PDF for the user to attach
      const waText = encodeURIComponent(
        `${summary}\n\nThe detailed PDF has been downloaded to your device — please attach it here.\n\nGenerated via Rubicon Finservices — www.rubiconfinservices.com`
      );
      const waUrl = `https://wa.me/?text=${waText}`;
      const waWin = window.open(waUrl, '_blank', 'noopener,noreferrer');
      // If popup was blocked, navigate current tab to WhatsApp as last resort
      if (!waWin) {
        window.location.href = waUrl;
        return;
      }
      doc.save(filename);
    } else {
      doc.save(filename);
    }
  };

  // SIP Calculation
  const sipResults = calculateSIP(sipMonthly, sipRate, sipYears);

  // Also compute end-of-month (Annuity Ordinary) FV for comparison.
  const sipFVOrdinary = (() => {
    const r = sipRate / 12 / 100;
    const n = sipYears * 12;
    if (r === 0) return sipMonthly * n;
    return Math.round(sipMonthly * ((Math.pow(1 + r, n) - 1) / r));
  })();

  // Retirement Calculation
  const calculateRetirement = () => {
    const yearsToRetirement = retirementAge - currentAge;
    const retirementYears = lifeExpectancy - retirementAge;
    
    // Future value of current expenses
    const futureMonthlyExpenses = monthlyExpenses * Math.pow((1 + inflationRate / 100), yearsToRetirement);
    const annualExpenses = futureMonthlyExpenses * 12;
    
    // Corpus needed at retirement (considering inflation during retirement)
    const avgInflationDuringRetirement = inflationRate / 100;
    const realReturn = ((1 + expectedReturn / 100) / (1 + avgInflationDuringRetirement)) - 1;
    const corpusNeeded = (annualExpenses / realReturn) * (1 - Math.pow(1 + realReturn, -retirementYears));
    
    // Monthly savings required
    const monthlyRate = expectedReturn / 100 / 12;
    const months = yearsToRetirement * 12;
    const monthlySavingsRequired = (corpusNeeded * monthlyRate) / (Math.pow(1 + monthlyRate, months) - 1);
    
    return {
      corpusNeeded,
      monthlySavingsRequired,
      futureMonthlyExpenses,
      yearsToRetirement
    };
  };

  const retirementResults = calculateRetirement();

  // XIRR Calculation using Newton-Raphson method
  const calculateXIRR = (cashFlows, dates, guess = 0.1) => {
    const maxIterations = 100;
    const tolerance = 0.0001;
    
    let rate = guess;
    for (let i = 0; i < maxIterations; i++) {
      let npv = 0;
      let dnpv = 0;
      
      for (let j = 0; j < cashFlows.length; j++) {
        const days = (dates[j] - dates[0]) / (1000 * 60 * 60 * 24);
        const years = days / 365;
        npv += cashFlows[j] / Math.pow(1 + rate, years);
        dnpv -= years * cashFlows[j] / Math.pow(1 + rate, years + 1);
      }
      
      const newRate = rate - npv / dnpv;
      
      if (Math.abs(newRate - rate) < tolerance) {
        return newRate * 100; // Return as percentage
      }
      
      rate = newRate;
    }
    
    return rate * 100;
  };

  // STP Calculation with XIRR
  const calculateSTP = () => {
    const months = stpDuration;
    const monthlyDebtRate = stpDebtReturn / 100 / 12;
    const monthlyEquityRate = stpEquityReturn / 100 / 12;
    
    let debtBalance = stpInitialAmount;
    let equityBalance = 0;
    let totalTransferred = 0;
    
    // Track cash flows and dates for XIRR
    const cashFlows = [-stpInitialAmount]; // Initial investment as negative
    const dates = [new Date()];
    
    for (let i = 0; i < months; i++) {
      // Apply return on debt fund
      debtBalance = debtBalance * (1 + monthlyDebtRate);
      
      // Transfer from debt to equity
      if (debtBalance >= stpTransferAmount) {
        debtBalance -= stpTransferAmount;
        equityBalance += stpTransferAmount;
        totalTransferred += stpTransferAmount;
      }
      
      // Apply return on equity fund
      equityBalance = equityBalance * (1 + monthlyEquityRate);
    }
    
    const finalCorpus = debtBalance + equityBalance;
    const totalGains = finalCorpus - stpInitialAmount;
    
    // Add final value to cash flows for XIRR
    cashFlows.push(finalCorpus);
    const finalDate = new Date();
    finalDate.setMonth(finalDate.getMonth() + months);
    dates.push(finalDate);
    
    // Calculate XIRR
    let xirr = 0;
    try {
      xirr = calculateXIRR(cashFlows, dates);
    } catch (e) {
      // If XIRR calculation fails, use simple annualized return
      const years = months / 12;
      xirr = (Math.pow(finalCorpus / stpInitialAmount, 1 / years) - 1) * 100;
    }
    
    return {
      finalCorpus,
      debtBalance,
      equityBalance,
      totalTransferred,
      totalGains,
      xirr
    };
  };

  const stpResults = calculateSTP();

  // SWP Calculation
  const calculateSWP = () => {
    const monthlyRate = swpExpectedReturn / 100 / 12;
    let balance = swpInitialAmount;
    let totalWithdrawn = 0;
    let monthsSurvived = 0;
    let ranOutEarly = false;

    const months = Math.min(swpDuration, 600); // Cap at 50 years
    let currentWithdrawal = swpMonthlyWithdrawal;

    for (let month = 1; month <= months; month++) {
      // Step up withdrawal at the start of each year (after the first year)
      if (month > 1 && (month - 1) % 12 === 0 && swpInflation > 0) {
        currentWithdrawal = currentWithdrawal * (1 + swpInflation / 100);
      }

      // Add returns to balance
      balance = balance * (1 + monthlyRate);

      // Withdraw inflation-adjusted amount
      if (balance >= currentWithdrawal) {
        balance -= currentWithdrawal;
        totalWithdrawn += currentWithdrawal;
        monthsSurvived = month;
      } else {
        totalWithdrawn += balance;
        balance = 0;
        monthsSurvived = month;
        ranOutEarly = true;
        break;
      }
    }

    // Inflation-adjusted (real) value of remaining balance, in today's rupees
    const years = monthsSurvived / 12;
    const realFinalBalance = swpInflation > 0
      ? balance / Math.pow(1 + swpInflation / 100, years)
      : balance;
    // Last month's withdrawal in nominal terms (useful info)
    const finalMonthlyWithdrawal = currentWithdrawal;

    return {
      finalBalance: Math.max(0, balance),
      realFinalBalance: Math.max(0, realFinalBalance),
      totalWithdrawn,
      months: monthsSurvived,
      ranOutEarly,
      finalMonthlyWithdrawal
    };
  };

  const swpResults = calculateSWP();

  // Arabpati Calculation (₹100 Crore Goal)
  const calculateArabpati = () => {
    const targetAmount = 1000000000; // ₹100 Crore
    const years = arabpatiTargetAge - arabpatiCurrentAge;
    const months = years * 12;
    const monthlyRate = arabpatiExpectedReturn / 100 / 12;
    
    // Calculate future value with SIP
    let futureValue = arabpatiInitialAmount;
    
    // Add returns on initial amount
    futureValue = futureValue * Math.pow(1 + monthlyRate, months);
    
    // Add SIP contributions
    const sipFutureValue = arabpatiMonthlySIP * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
    
    futureValue += sipFutureValue;
    
    const totalInvested = arabpatiInitialAmount + (arabpatiMonthlySIP * months);
    const gains = futureValue - totalInvested;
    const progressPercent = Math.min((futureValue / targetAmount) * 100, 100);
    const shortfall = Math.max(targetAmount - futureValue, 0);
    
    // Calculate required SIP if goal not met
    let requiredSIP = 0;
    if (futureValue < targetAmount) {
      // FV = P × [((1 + r)^n - 1) / r] × (1 + r)
      // Solve for P (monthly SIP)
      const fvNeeded = targetAmount - (arabpatiInitialAmount * Math.pow(1 + monthlyRate, months));
      requiredSIP = (fvNeeded * monthlyRate) / ((Math.pow(1 + monthlyRate, months) - 1) * (1 + monthlyRate));
    }
    
    return {
      futureValue,
      totalInvested,
      gains,
      progressPercent,
      yearsToGo: years,
      goalAchieved: futureValue >= targetAmount,
      shortfall,
      requiredSIP
    };
  };

  const arabpatiResults = calculateArabpati();

  // Child Education Calculator calculation
  const calculateChildEducation = () => {
    const monthlyRate = childExpectedReturn / 12 / 100;
    const results = children.map((child) => {
      const years = Math.max(1, child.educationAge - child.currentAge);
      const months = years * 12;
      const todayCost = child.educationType === 'others'
        ? Math.max(0, Number(child.customCost) || 0)
        : EDUCATION_PRESETS[child.educationType].cost;
      const futureCost = todayCost * Math.pow(1 + childInflation / 100, years);
      const monthlySIP = monthlyRate === 0
        ? futureCost / months
        : futureCost / (((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate));
      const totalInvestment = monthlySIP * months;
      return {
        ...child,
        todayCost,
        futureCost,
        monthlySIP,
        totalInvestment,
        years,
        wealthGain: futureCost - totalInvestment
      };
    });
    const totalMonthlySIP = results.reduce((s, c) => s + c.monthlySIP, 0);
    const totalInvestment = results.reduce((s, c) => s + c.totalInvestment, 0);
    const totalFutureCost = results.reduce((s, c) => s + c.futureCost, 0);
    return { children: results, totalMonthlySIP, totalInvestment, totalFutureCost };
  };
  const educationResults = calculateChildEducation();

  // Goal Planner calculation
  const calculateGoal = () => {
    const months = goalYears * 12;
    const monthlyRate = goalReturn / 12 / 100;
    const futureCost = goalCostToday * Math.pow(1 + goalInflation / 100, goalYears);
    const existingFV = goalExistingSavings * Math.pow(1 + goalReturn / 100, goalYears);
    const shortfall = Math.max(0, futureCost - existingFV);
    const monthlySIP = months <= 0 || shortfall <= 0
      ? 0
      : (monthlyRate === 0
        ? shortfall / months
        : shortfall / (((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate)));
    const totalInvestment = monthlySIP * months;
    const wealthGain = futureCost - totalInvestment - goalExistingSavings;
    return { futureCost, existingFV, shortfall, monthlySIP, totalInvestment, wealthGain };
  };
  const goalResults = calculateGoal();

  // PDF Download Functions
  const downloadSIPPDF = async (action = 'download') => {
    const doc = new jsPDF();
    let yPos = addPDFHeader(doc, 'SIP Calculator Report');
    const pageWidth = doc.internal.pageSize.getWidth();

    // Date
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, 14, yPos);
    yPos += 10;

    // Investment Details Section
    doc.setFillColor(240, 240, 255);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 51, 102);
    doc.text('INVESTMENT DETAILS', 16, yPos + 5);
    yPos += 12;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    const details = [
      ['Monthly SIP Amount:', formatCurrencyForPDF(sipMonthly)],
      ['Investment Period:', `${sipYears} Years (${sipYears * 12} Months)`],
      ['Expected Rate of Return*:', `${sipRate}% p.a.`]
    ];

    details.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 16, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 80, yPos);
      yPos += 6;
    });

    yPos += 5;

    // Results Section
    doc.setFillColor(240, 255, 240);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 102, 0);
    doc.text('PROJECTED RESULTS', 16, yPos + 5);
    yPos += 12;

    doc.setFillColor(255, 250, 230);
    doc.rect(14, yPos, pageWidth - 28, 25, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('Total Investment:', 18, yPos + 5);
    doc.setTextColor(0, 51, 102);
    doc.text(formatCurrencyForPDF(sipResults.totalInvestment), 80, yPos + 5);
    
    doc.setTextColor(0, 0, 0);
    doc.text('Estimated Returns:', 18, yPos + 12);
    doc.setTextColor(0, 128, 0);
    doc.text(formatCurrencyForPDF(sipResults.estimatedReturns), 80, yPos + 12);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(139, 69, 19);
    doc.text('Estimated Maturity Value:', 18, yPos + 20);
    doc.setTextColor(0, 100, 0);
    doc.text(formatCurrencyForPDF(sipResults.futureValue), 80, yPos + 20);
    
    yPos += 35;

    // Comprehensive Disclaimer
    doc.setFillColor(255, 245, 245);
    doc.rect(14, yPos, pageWidth - 28, 32, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(150, 0, 0);
    doc.text('IMPORTANT DISCLAIMER', 16, yPos + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 0, 0);
    
    const disclaimerLines = [
      '* Assumed Rate of Return: The projected values are based on an assumed rate of return and are for illustrative purposes only.',
      '  Actual returns may vary significantly based on market conditions and fund performance.',
      '',
      '* Mutual Fund Investments are subject to market risks. Please read all scheme-related documents carefully before investing.',
      '',
      '* Past performance is not indicative of future results. The value of investments may fluctuate and can go down as well as up.',
      '',
      '* This calculator is a tool for financial planning only and should not be construed as investment advice.',
      '  Consult with qualified financial advisors for personalized investment recommendations.'
    ];
    
    let disclaimerY = yPos + 10;
    disclaimerLines.forEach(line => {
      doc.text(line, 16, disclaimerY);
      disclaimerY += 3;
    });

    addPDFFooter(doc);
    const sipSummary = `Hi! Here's my SIP projection from Rubicon Finservices:\n\nMonthly SIP: ${formatCurrencyForPDF(sipMonthly)}\nDuration: ${sipYears} years\nExpected Return: ${sipRate}% p.a.\n\nTotal Invested: ${formatCurrencyForPDF(sipResults.totalInvestment)}\nEstimated Returns: ${formatCurrencyForPDF(sipResults.estimatedReturns)}\nMaturity Value: ${formatCurrencyForPDF(sipResults.futureValue)}`;
    await handlePdfAction(doc, 'SIP_Calculator_Report.pdf', sipSummary, action);
  };

  const downloadRetirementPDF = async (action = 'download') => {
    const doc = new jsPDF();
    let yPos = addPDFHeader(doc, 'Retirement Planning Report');
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, 14, yPos);
    yPos += 10;

    // Personal Details
    doc.setFillColor(240, 240, 255);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 51, 102);
    doc.text('PERSONAL DETAILS', 16, yPos + 5);
    yPos += 12;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    const personal = [
      ['Current Age:', `${currentAge} Years`],
      ['Retirement Age:', `${retirementAge} Years`],
      ['Life Expectancy:', `${lifeExpectancy} Years`],
      ['Years to Retirement:', `${retirementResults.yearsToRetirement} Years`]
    ];

    personal.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 16, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 80, yPos);
      yPos += 6;
    });

    yPos += 5;

    // Assumptions
    doc.setFillColor(255, 245, 230);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(139, 69, 19);
    doc.text('ASSUMPTIONS', 16, yPos + 5);
    yPos += 12;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    const assumptions = [
      ['Current Monthly Expenses:', formatCurrencyForPDF(monthlyExpenses)],
      ['Expected Inflation:', `${inflationRate}% p.a.`],
      ['Expected Return on Investment:', `${expectedReturn}% p.a.`],
      ['Future Monthly Expenses:', formatCurrencyForPDF(retirementResults.futureMonthlyExpenses)]
    ];

    assumptions.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 16, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 90, yPos);
      yPos += 6;
    });

    yPos += 5;

    // Results
    doc.setFillColor(240, 255, 240);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 102, 0);
    doc.text('RETIREMENT CORPUS REQUIRED', 16, yPos + 5);
    yPos += 12;

    doc.setFillColor(255, 250, 230);
    doc.rect(14, yPos, pageWidth - 28, 15, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(139, 69, 19);
    doc.text('Corpus Needed at Retirement:', 18, yPos + 5);
    doc.setTextColor(0, 100, 0);
    doc.text(formatCurrencyForPDF(retirementResults.corpusNeeded), 100, yPos + 5);
    
    doc.setTextColor(0, 51, 102);
    doc.setFontSize(11);
    doc.text('Monthly Savings Required:', 18, yPos + 12);
    doc.setTextColor(255, 100, 0);
    doc.text(formatCurrencyForPDF(retirementResults.monthlySavingsRequired), 100, yPos + 12);

    yPos += 25;

    // Comprehensive Disclaimer
    doc.setFillColor(255, 245, 245);
    doc.rect(14, yPos, pageWidth - 28, 32, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(150, 0, 0);
    doc.text('IMPORTANT DISCLAIMER', 16, yPos + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 0, 0);
    
    const disclaimerLines = [
      '* Assumed Returns & Inflation: Projections are based on assumed rates and are for illustrative purposes only.',
      '  Actual returns and inflation may vary significantly based on market conditions.',
      '',
      '* Mutual Fund Investments are subject to market risks. Please read all scheme-related documents carefully before investing.',
      '',
      '* Past performance is not indicative of future results. The value of investments may fluctuate.',
      '',
      '* This calculator is for financial planning only and should not be construed as investment advice.',
      '  Consult with qualified financial advisors for personalized retirement planning recommendations.'
    ];
    
    let disclaimerY = yPos + 10;
    disclaimerLines.forEach(line => {
      doc.text(line, 16, disclaimerY);
      disclaimerY += 3;
    });

    addPDFFooter(doc);
    const retSummary = `Hi! Here's my Retirement plan from Rubicon Finservices:\n\nCurrent Age: ${currentAge} | Retirement Age: ${retirementAge}\nMonthly Expenses (today): ${formatCurrencyForPDF(monthlyExpenses)}\nCorpus Required: ${formatCurrencyForPDF(retirementResults.corpusNeeded)}\nMonthly SIP Needed: ${formatCurrencyForPDF(retirementResults.monthlySavingsRequired)}`;
    await handlePdfAction(doc, 'Retirement_Planning_Report.pdf', retSummary, action);
  };

  const downloadSTPPDF = async (action = 'download') => {
    const doc = new jsPDF();
    let yPos = addPDFHeader(doc, 'STP Calculator Report');
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, 14, yPos);
    yPos += 10;

    // Investment Details
    doc.setFillColor(240, 240, 255);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 51, 102);
    doc.text('STP PARAMETERS', 16, yPos + 5);
    yPos += 12;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    const details = [
      ['Initial Investment:', formatCurrencyForPDF(stpInitialAmount)],
      ['Monthly Transfer:', formatCurrencyForPDF(stpTransferAmount)],
      ['Transfer Duration:', `${stpDuration} Months`],
      ['Debt Fund Return:', `${stpDebtReturn}% p.a.`],
      ['Equity Fund Return:', `${stpEquityReturn}% p.a.`]
    ];

    details.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 16, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 80, yPos);
      yPos += 6;
    });

    yPos += 5;

    // Results
    doc.setFillColor(240, 255, 240);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 102, 0);
    doc.text('PROJECTED RESULTS', 16, yPos + 5);
    yPos += 12;

    doc.setFillColor(255, 250, 230);
    doc.rect(14, yPos, pageWidth - 28, 30, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('Total Transferred:', 18, yPos + 5);
    doc.setTextColor(0, 51, 102);
    doc.text(formatCurrencyForPDF(stpResults.totalTransferred), 80, yPos + 5);
    
    doc.setTextColor(0, 0, 0);
    doc.text('Final Corpus:', 18, yPos + 12);
    doc.setTextColor(0, 128, 0);
    doc.text(formatCurrencyForPDF(stpResults.finalCorpus), 80, yPos + 12);
    
    doc.setTextColor(0, 0, 0);
    doc.text('Total Gains:', 18, yPos + 19);
    doc.setTextColor(0, 100, 0);
    doc.text(formatCurrencyForPDF(stpResults.totalGains), 80, yPos + 19);
    
    doc.setTextColor(139, 69, 19);
    doc.text('Effective XIRR:', 18, yPos + 26);
    doc.setTextColor(255, 100, 0);
    doc.text(`${stpResults.xirr.toFixed(2)}% p.a.`, 80, yPos + 26);

    yPos += 40;

    // Comprehensive Disclaimer
    doc.setFillColor(255, 245, 245);
    doc.rect(14, yPos, pageWidth - 28, 32, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(150, 0, 0);
    doc.text('IMPORTANT DISCLAIMER', 16, yPos + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 0, 0);
    
    const disclaimerLines = [
      '* Assumed Returns: Projections are based on assumed debt and equity fund returns and are for illustrative purposes only.',
      '  Actual returns may vary significantly based on market conditions and fund performance.',
      '',
      '* Mutual Fund Investments are subject to market risks. Please read all scheme-related documents carefully before investing.',
      '',
      '* XIRR is a mathematical calculation and does not guarantee future returns.',
      '',
      '* This calculator is for financial planning only and should not be construed as investment advice.',
      '  Consult with qualified financial advisors for personalized investment recommendations.'
    ];
    
    let disclaimerY = yPos + 10;
    disclaimerLines.forEach(line => {
      doc.text(line, 16, disclaimerY);
      disclaimerY += 3;
    });

    addPDFFooter(doc);
    const stpSummary = `Hi! Here's my STP projection from Rubicon Finservices:\n\nInitial Amount: ${formatCurrencyForPDF(stpInitialAmount)}\nMonthly Transfer: ${formatCurrencyForPDF(stpTransferAmount)}\nDuration: ${stpDuration} months\n\nFinal Corpus: ${formatCurrencyForPDF(stpResults.finalCorpus)}\nTotal Gains: ${formatCurrencyForPDF(stpResults.totalGains)}\nXIRR: ${stpResults.xirr.toFixed(2)}% p.a.`;
    await handlePdfAction(doc, 'STP_Calculator_Report.pdf', stpSummary, action);
  };

  const downloadSWPPDF = async (action = 'download') => {
    const doc = new jsPDF();
    let yPos = addPDFHeader(doc, 'SWP Calculator Report');
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, 14, yPos);
    yPos += 10;

    // Investment Details
    doc.setFillColor(240, 240, 255);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 51, 102);
    doc.text('SWP PARAMETERS', 16, yPos + 5);
    yPos += 12;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    const details = [
      ['Initial Investment:', formatCurrencyForPDF(swpInitialAmount)],
      ['Starting Monthly Withdrawal:', formatCurrencyForPDF(swpMonthlyWithdrawal)],
      ['Withdrawal Duration:', `${Math.floor(swpDuration / 12)} Years ${swpDuration % 12} Months`],
      ['Expected Return:', `${swpExpectedReturn}% p.a.`],
      ['Inflation (annual step-up):', `${swpInflation}% p.a.`]
    ];

    details.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 16, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 80, yPos);
      yPos += 6;
    });

    yPos += 5;

    // Results
    doc.setFillColor(240, 255, 240);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 102, 0);
    doc.text('WITHDRAWAL SUMMARY', 16, yPos + 5);
    yPos += 12;

    doc.setFillColor(255, 250, 230);
    doc.rect(14, yPos, pageWidth - 28, 32, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('Total Withdrawn:', 18, yPos + 5);
    doc.setTextColor(0, 51, 102);
    doc.text(formatCurrencyForPDF(swpResults.totalWithdrawn), 80, yPos + 5);
    
    doc.setTextColor(0, 0, 0);
    doc.text('Final Balance:', 18, yPos + 12);
    doc.setTextColor(0, 128, 0);
    doc.text(formatCurrencyForPDF(swpResults.finalBalance), 80, yPos + 12);

    doc.setTextColor(0, 0, 0);
    doc.text('Corpus Lasted:', 18, yPos + 19);
    doc.setTextColor(180, 80, 0);
    doc.text(`${Math.floor(swpResults.months / 12)} Years ${swpResults.months % 12} Months${swpResults.ranOutEarly ? ' (depleted early)' : ''}`, 80, yPos + 19);

    if (swpInflation > 0) {
      doc.setTextColor(0, 0, 0);
      doc.text('Final Month Withdrawal:', 18, yPos + 26);
      doc.setTextColor(150, 30, 90);
      doc.text(formatCurrencyForPDF(swpResults.finalMonthlyWithdrawal), 80, yPos + 26);
    }

    yPos += 40;

    // Comprehensive Disclaimer
    doc.setFillColor(255, 245, 245);
    doc.rect(14, yPos, pageWidth - 28, 32, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(150, 0, 0);
    doc.text('IMPORTANT DISCLAIMER', 16, yPos + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 0, 0);
    
    const disclaimerLines = [
      '* Assumed Returns: Withdrawal sustainability depends on actual fund performance. Projections are illustrative only.',
      '  Actual returns may vary significantly based on market conditions.',
      '',
      '* Mutual Fund Investments are subject to market risks. Please read all scheme-related documents carefully before investing.',
      '',
      '* Withdrawal plan sustainability depends on market performance and may deplete earlier than projected.',
      '',
      '* This calculator is for financial planning only and should not be construed as investment advice.',
      '  Consult with qualified financial advisors for personalized withdrawal planning recommendations.'
    ];
    
    let disclaimerY = yPos + 10;
    disclaimerLines.forEach(line => {
      doc.text(line, 16, disclaimerY);
      disclaimerY += 3;
    });

    addPDFFooter(doc);
    const swpSummary = `Hi! Here's my SWP projection from Rubicon Finservices:\n\nInitial Investment: ${formatCurrencyForPDF(swpInitialAmount)}\nStarting Monthly Withdrawal: ${formatCurrencyForPDF(swpMonthlyWithdrawal)}\nInflation Step-up: ${swpInflation}% p.a.\nReturn: ${swpExpectedReturn}% p.a.\n\nTotal Withdrawn: ${formatCurrencyForPDF(swpResults.totalWithdrawn)}\nRemaining Balance: ${formatCurrencyForPDF(swpResults.finalBalance)}\nCorpus lasted: ${Math.floor(swpResults.months / 12)} years ${swpResults.months % 12} months`;
    await handlePdfAction(doc, 'SWP_Calculator_Report.pdf', swpSummary, action);
  };

  const downloadArabpatiPDF = async (action = 'download') => {
    const doc = new jsPDF();
    let yPos = addPDFHeader(doc, 'Arabpati Calculator Report');
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, 14, yPos);
    yPos += 10;

    // Goal
    doc.setFillColor(255, 245, 230);
    doc.rect(14, yPos, pageWidth - 28, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 100, 0);
    doc.text('TARGET GOAL: Rs. 100 Crore (Rs. 1 Arab)', pageWidth / 2, yPos + 7, { align: 'center' });
    yPos += 15;

    // Investment Details
    doc.setFillColor(240, 240, 255);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 51, 102);
    doc.text('INVESTMENT JOURNEY', 16, yPos + 5);
    yPos += 12;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    const details = [
      ['Current Age:', `${arabpatiCurrentAge} Years`],
      ['Target Age:', `${arabpatiTargetAge} Years`],
      ['Years to Go:', `${arabpatiResults.yearsToGo} Years`],
      ['Initial Investment:', formatCurrencyForPDF(arabpatiInitialAmount)],
      ['Monthly SIP:', formatCurrencyForPDF(arabpatiMonthlySIP)],
      ['Expected Return:', `${arabpatiExpectedReturn}% p.a.`]
    ];

    details.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 16, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 80, yPos);
      yPos += 6;
    });

    yPos += 5;

    // Results
    doc.setFillColor(240, 255, 240);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 102, 0);
    doc.text('PROJECTED WEALTH', 16, yPos + 5);
    yPos += 12;

    doc.setFillColor(255, 250, 230);
    doc.rect(14, yPos, pageWidth - 28, 25, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('Total Investment:', 18, yPos + 5);
    doc.setTextColor(0, 51, 102);
    doc.text(formatCurrencyForPDF(arabpatiResults.totalInvested), 80, yPos + 5);
    
    doc.setTextColor(0, 0, 0);
    doc.text('Estimated Future Value:', 18, yPos + 12);
    doc.setTextColor(0, 128, 0);
    doc.text(formatCurrencyForPDF(arabpatiResults.futureValue), 80, yPos + 12);
    
    doc.setTextColor(139, 69, 19);
    doc.text('Progress to Goal:', 18, yPos + 19);
    doc.setTextColor(255, 100, 0);
    doc.text(`${arabpatiResults.progressPercent.toFixed(2)}%`, 80, yPos + 19);

    yPos += 30;

    if (!arabpatiResults.goalAchieved) {
      doc.setFillColor(255, 240, 240);
      doc.rect(14, yPos, pageWidth - 28, 15, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(255, 0, 0);
      doc.text('Goal Not Achieved with Current Plan', 18, yPos + 5);
      doc.setFont('helvetica', 'normal');
      doc.text('Shortfall:', 18, yPos + 10);
      doc.text(formatCurrencyForPDF(arabpatiResults.shortfall), 80, yPos + 10);
      doc.text('Required Monthly SIP:', 18, yPos + 14);
      doc.text(formatCurrencyForPDF(arabpatiResults.requiredSIP), 80, yPos + 14);
      yPos += 20;
    } else {
      doc.setFillColor(240, 255, 240);
      doc.rect(14, yPos, pageWidth - 28, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(0, 128, 0);
      doc.text('Congratulations! Goal Achieved!', pageWidth / 2, yPos + 5, { align: 'center' });
      yPos += 13;
    }

    // Comprehensive Disclaimer
    doc.setFillColor(255, 245, 245);
    doc.rect(14, yPos, pageWidth - 28, 32, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(150, 0, 0);
    doc.text('IMPORTANT DISCLAIMER', 16, yPos + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 0, 0);
    
    const disclaimerLines = [
      '* Assumed Returns: Projections are based on assumed rate of return and are for illustrative purposes only.',
      '  Actual wealth accumulation may vary significantly based on market performance.',
      '',
      '* Mutual Fund Investments are subject to market risks. Please read all scheme-related documents carefully before investing.',
      '',
      '* Past performance is not indicative of future results. The value of investments may fluctuate.',
      '',
      '* This calculator is for financial goal planning only and should not be construed as investment advice.',
      '  Consult with qualified financial advisors for personalized wealth creation recommendations.'
    ];
    
    let disclaimerY = yPos + 10;
    disclaimerLines.forEach(line => {
      doc.text(line, 16, disclaimerY);
      disclaimerY += 3;
    });

    addPDFFooter(doc);
    const arabSummary = `Hi! Here's my Arabpati (Rs.100 Cr) plan from Rubicon Finservices:\n\nCurrent Age: ${arabpatiCurrentAge} | Target Age: ${arabpatiTargetAge}\nMonthly SIP: ${formatCurrencyForPDF(arabpatiMonthlySIP)}\nExpected Return: ${arabpatiExpectedReturn}% p.a.\n\nTotal Invested: ${formatCurrencyForPDF(arabpatiResults.totalInvested)}\nFuture Value: ${formatCurrencyForPDF(arabpatiResults.futureValue)}\nProgress to Rs.100 Cr: ${arabpatiResults.progressPercent.toFixed(2)}%`;
    await handlePdfAction(doc, 'Arabpati_Calculator_Report.pdf', arabSummary, action);
  };

  const downloadEducationPDF = async (action = 'download') => {
    const doc = new jsPDF();
    let yPos = addPDFHeader(doc, 'Child Education Planning Report');
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, 14, yPos);
    yPos += 8;

    // Common assumptions
    doc.setFillColor(240, 240, 255);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 51, 102);
    doc.text('PLANNING ASSUMPTIONS', 16, yPos + 5);
    yPos += 11;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Education Inflation: ${childInflation}% p.a.`, 16, yPos);
    yPos += 5;
    doc.text(`Expected Investment Return: ${childExpectedReturn}% p.a.`, 16, yPos);
    yPos += 8;

    // Per-child breakdown
    educationResults.children.forEach((c, idx) => {
      doc.setFillColor(255, 240, 245);
      doc.rect(14, yPos, pageWidth - 28, 7, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(180, 30, 90);
      doc.text(`${c.name.toUpperCase()} - ${EDUCATION_PRESETS[c.educationType].label}`, 16, yPos + 5);
      yPos += 11;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      const rows = [
        ['Current Age:', `${c.currentAge} years`],
        ['Education Start Age:', `${c.educationAge} years`],
        ['Years to Goal:', `${c.years} years`],
        ['Cost Today:', formatCurrencyForPDF(c.todayCost)],
        ['Future Cost (at goal):', formatCurrencyForPDF(c.futureCost)],
        ['Required Monthly SIP:', formatCurrencyForPDF(c.monthlySIP)],
        ['Total Investment:', formatCurrencyForPDF(c.totalInvestment)],
        ['Expected Wealth Gain:', formatCurrencyForPDF(c.wealthGain)]
      ];
      rows.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(label, 18, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(value, 90, yPos);
        yPos += 5;
      });
      yPos += 4;
    });

    // Combined totals
    if (educationResults.children.length > 1) {
      doc.setFillColor(240, 255, 240);
      doc.rect(14, yPos, pageWidth - 28, 7, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(0, 102, 0);
      doc.text('COMBINED TOTAL (ALL CHILDREN)', 16, yPos + 5);
      yPos += 11;
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('Total Monthly SIP:', 18, yPos);
      doc.setTextColor(0, 102, 0);
      doc.text(formatCurrencyForPDF(educationResults.totalMonthlySIP), 90, yPos);
      yPos += 6;
      doc.setTextColor(0, 0, 0);
      doc.text('Total Investment:', 18, yPos);
      doc.setTextColor(0, 51, 102);
      doc.text(formatCurrencyForPDF(educationResults.totalInvestment), 90, yPos);
      yPos += 6;
      doc.setTextColor(0, 0, 0);
      doc.text('Total Future Goal:', 18, yPos);
      doc.setTextColor(180, 30, 90);
      doc.text(formatCurrencyForPDF(educationResults.totalFutureCost), 90, yPos);
      yPos += 10;
    }

    // Disclaimer
    doc.setFillColor(255, 245, 245);
    doc.rect(14, yPos, pageWidth - 28, 28, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(150, 0, 0);
    doc.text('IMPORTANT DISCLAIMER', 16, yPos + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 0, 0);
    const lines = [
      '* Education costs and inflation rates are illustrative. Actual costs vary by institution and country.',
      '* Mutual fund investments are subject to market risks. Read all scheme-related documents carefully.',
      '* Projections are based on assumed rates of return and do not guarantee future results.',
      '* Consult a qualified financial advisor for personalized education planning.'
    ];
    let dy = yPos + 10;
    lines.forEach(l => { doc.text(l, 16, dy); dy += 4; });

    addPDFFooter(doc);
    const eduSummary = `Hi! Here's my child education plan from Rubicon Finservices:\n\n${educationResults.children.map(c => `${c.name} - ${EDUCATION_PRESETS[c.educationType].label}\n  Cost Today: ${formatCurrencyForPDF(c.todayCost)}\n  Future Cost: ${formatCurrencyForPDF(c.futureCost)}\n  Required SIP: ${formatCurrencyForPDF(c.monthlySIP)}/month`).join('\n\n')}\n\nTotal Monthly SIP Required: ${formatCurrencyForPDF(educationResults.totalMonthlySIP)}`;
    await handlePdfAction(doc, 'Child_Education_Plan.pdf', eduSummary, action);
  };

  const downloadGoalPDF = async (action = 'download') => {
    const doc = new jsPDF();
    let yPos = addPDFHeader(doc, 'Goal Planning Report');
    const pageWidth = doc.internal.pageSize.getWidth();
    const presetLabel = GOAL_PRESETS[goalType].label;

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, 14, yPos);
    yPos += 8;

    doc.setFillColor(240, 245, 255);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 51, 102);
    doc.text(`GOAL: ${(goalName || presetLabel).toUpperCase()}`, 16, yPos + 5);
    yPos += 11;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const rows = [
      ['Goal Category:', presetLabel],
      ['Cost Today:', formatCurrencyForPDF(goalCostToday)],
      ['Time to Goal:', `${goalYears} year(s)`],
      ['Inflation Rate:', `${goalInflation}% p.a.`],
      ['Expected Return:', `${goalReturn}% p.a.`],
      ['Existing Savings:', formatCurrencyForPDF(goalExistingSavings)]
    ];
    rows.forEach(([k, v]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(k, 18, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(v, 90, yPos);
      yPos += 6;
    });
    yPos += 4;

    doc.setFillColor(255, 240, 230);
    doc.rect(14, yPos, pageWidth - 28, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(180, 80, 0);
    doc.text('PROJECTIONS', 16, yPos + 5);
    yPos += 11;

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const out = [
      ['Future Cost (at goal):', formatCurrencyForPDF(goalResults.futureCost)],
      ['Existing Savings (FV):', formatCurrencyForPDF(goalResults.existingFV)],
      ['Shortfall to Cover:', formatCurrencyForPDF(goalResults.shortfall)],
      ['Required Monthly SIP:', formatCurrencyForPDF(goalResults.monthlySIP)],
      ['Total Investment over Goal:', formatCurrencyForPDF(goalResults.totalInvestment)],
      ['Estimated Wealth Gain:', formatCurrencyForPDF(goalResults.wealthGain)]
    ];
    out.forEach(([k, v]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(k, 18, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(v, 90, yPos);
      yPos += 6;
    });
    yPos += 6;

    // Disclaimer
    doc.setFillColor(255, 245, 245);
    doc.rect(14, yPos, pageWidth - 28, 28, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(150, 0, 0);
    doc.text('IMPORTANT DISCLAIMER', 16, yPos + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 0, 0);
    const dlines = [
      '* Projections are illustrative. Actual costs and returns may differ significantly.',
      '* Mutual fund investments are subject to market risks. Read scheme documents carefully.',
      '* Inflation rate assumed at user input - actual inflation may vary year-to-year.',
      '* Past performance is not indicative of future returns.'
    ];
    let dy = yPos + 10;
    dlines.forEach(l => { doc.text(l, 16, dy); dy += 4; });

    addPDFFooter(doc);
    const summary = `Hi! Here's my goal plan from Rubicon Finservices:\n\nGoal: ${goalName || presetLabel}\nCost Today: ${formatCurrencyForPDF(goalCostToday)}\nTime to Goal: ${goalYears} years\n\nFuture Cost: ${formatCurrencyForPDF(goalResults.futureCost)}\nRequired Monthly SIP: ${formatCurrencyForPDF(goalResults.monthlySIP)}`;
    await handlePdfAction(doc, `Goal_Plan_${(goalName || presetLabel).replace(/\s+/g, '_')}.pdf`, summary, action);
  };

  return (
    <section id="calculator" className="py-12 sm:py-24 bg-slate-50">
      <div className="container mx-auto px-3 sm:px-4">
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-block bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Financial Calculators
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-6">
            Plan Your Financial Journey
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto px-2">
            Use our comprehensive calculators to plan your investments, retirement, and wealth creation strategy
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <Tabs defaultValue="sip" className="w-full">
            <TabsList className="grid w-full grid-cols-3 sm:grid-cols-7 gap-1 sm:gap-0 mb-6 sm:mb-8 bg-white p-1.5 sm:p-2 rounded-xl shadow-lg h-auto">
              <TabsTrigger value="sip" className="flex-col sm:flex-row gap-1 sm:gap-0 text-xs sm:text-sm font-semibold py-2 sm:py-2.5 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
                <TrendingUp className="sm:mr-1" size={16} />
                <span>SIP</span>
              </TabsTrigger>
              <TabsTrigger value="swp" className="flex-col sm:flex-row gap-1 sm:gap-0 text-xs sm:text-sm font-semibold py-2 sm:py-2.5 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
                <DollarSign className="sm:mr-1" size={16} />
                <span>SWP</span>
              </TabsTrigger>
              <TabsTrigger value="arabpati" className="flex-col sm:flex-row gap-1 sm:gap-0 text-xs sm:text-sm font-semibold py-2 sm:py-2.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white">
                <Calculator className="sm:mr-1" size={16} />
                <span>Arabpati</span>
              </TabsTrigger>
              <TabsTrigger value="retirement" className="flex-col sm:flex-row gap-1 sm:gap-0 text-xs sm:text-sm font-semibold py-2 sm:py-2.5 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
                <PiggyBank className="sm:mr-1" size={16} />
                <span>Retirement</span>
              </TabsTrigger>
              <TabsTrigger value="stp" className="flex-col sm:flex-row gap-1 sm:gap-0 text-xs sm:text-sm font-semibold py-2 sm:py-2.5 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
                <ArrowRightLeft className="sm:mr-1" size={16} />
                <span>STP</span>
              </TabsTrigger>
              <TabsTrigger value="education" className="flex-col sm:flex-row gap-1 sm:gap-0 text-xs sm:text-sm font-semibold py-2 sm:py-2.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-rose-600 data-[state=active]:text-white">
                <GraduationCap className="sm:mr-1" size={16} />
                <span>Education</span>
              </TabsTrigger>
              <TabsTrigger value="goal" className="flex-col sm:flex-row gap-1 sm:gap-0 text-xs sm:text-sm font-semibold py-2 sm:py-2.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white">
                <Target className="sm:mr-1" size={16} />
                <span>Goal Planner</span>
              </TabsTrigger>
            </TabsList>

            {/* SIP Calculator */}
            <TabsContent value="sip">
              <div className="grid lg:grid-cols-2 gap-4 sm:gap-8">
                <Card className="border-2 border-slate-200 shadow-xl">
                  <CardHeader className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <Calculator size={28} />
                      SIP Parameters
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-8 space-y-6 sm:space-y-8">
                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Monthly Investment</Label>
                        <ValueInput value={sipMonthly} onChange={(v) => setSipMonthly(v)} min={500} max={10000000} prefix="₹" colorCls="text-amber-600" testId="input-sipMonthly" />
                      </div>
                      <Slider
                        value={[sipMonthly]}
                        onValueChange={(value) => setSipMonthly(value[0])}
                        min={500}
                        max={10000000}
                        step={500}
                        className="py-4"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Expected Return (p.a.)</Label>
                        <ValueInput value={sipRate} onChange={(v) => setSipRate(v)} min={1} max={30} suffix="%" colorCls="text-amber-600" testId="input-sipRate" />
                      </div>
                      <Slider
                        value={[sipRate]}
                        onValueChange={(value) => setSipRate(value[0])}
                        min={1}
                        max={30}
                        step={0.5}
                        className="py-4"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Time Period</Label>
                        <ValueInput value={sipYears} onChange={(v) => setSipYears(v)} min={1} max={40} colorCls="text-amber-600" testId="input-sipYears" />
                      </div>
                      <Slider
                        value={[sipYears]}
                        onValueChange={(value) => setSipYears(value[0])}
                        min={1}
                        max={40}
                        step={1}
                        className="py-4"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-amber-200 shadow-xl bg-gradient-to-br from-amber-50 to-white">
                  <CardHeader className="bg-gradient-to-br from-amber-600 to-amber-700 text-white rounded-t-lg">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                        <TrendingUp size={28} />
                        Investment Returns
                      </CardTitle>
                      <MethodologyInfo title="SIP Formula" testId="sip-methodology-btn">
                        <p>We use the <strong>Annuity Due</strong> formula (SIP debited at the <em>start</em> of every month — same as your bank&apos;s auto-debit):</p>
                        <p className="font-mono bg-slate-100 p-2 rounded text-xs">FV = P × [((1+r)<sup>n</sup> − 1) / r] × (1+r)</p>
                        <p className="text-xs text-slate-500">Matches AMFI / Groww / Zerodha Coin. Some sites use Annuity Ordinary (end of month) which yields ~1% lower — shown alongside for transparency.</p>
                      </MethodologyInfo>
                    </div>
                  </CardHeader>
                  <CardContent className="p-8 space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="bg-white rounded-xl p-6 border-2 border-slate-100">
                        <DollarSign className="text-slate-600 mb-2" size={24} />
                        <p className="text-sm text-slate-600 mb-1">Total Investment</p>
                        <p className="text-2xl font-bold text-slate-900">{formatCurrency(sipResults.totalInvestment)}</p>
                      </div>
                      <div className="bg-white rounded-xl p-6 border-2 border-slate-100">
                        <Calendar className="text-slate-600 mb-2" size={24} />
                        <p className="text-sm text-slate-600 mb-1">Duration</p>
                        <p className="text-2xl font-bold text-slate-900">{sipYears * 12} Months</p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
                      <p className="text-sm text-green-700 font-semibold mb-2">Estimated Returns</p>
                      <p className="text-2xl sm:text-3xl font-bold text-green-900 break-words">{formatCurrency(sipResults.estimatedReturns)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl p-8 border-2 border-amber-300">
                      <p className="text-base text-amber-800 font-semibold mb-1">Total Value (Annuity Due · start of month)</p>
                      <p className="text-3xl sm:text-5xl font-bold text-amber-900 mb-2 break-words">{formatCurrency(sipResults.futureValue)}</p>
                      <p className="text-sm text-amber-700 mb-3">
                        Your investment will grow to this amount in {sipYears} years
                      </p>
                      <div className="border-t border-amber-300 pt-3">
                        <p className="text-xs text-amber-800 font-semibold mb-1">For comparison (Annuity Ordinary · end of month)</p>
                        <p className="text-lg sm:text-xl font-bold text-amber-900 break-words" data-testid="sip-ordinary-value">
                          {formatCurrency(sipFVOrdinary)}
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          Some calculators use this end-of-month basis. Difference is one extra month of compounding.
                        </p>
                      </div>
                    </div>
                    
                    {/* PDF Action Buttons */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Button 
                        onClick={() => downloadSIPPDF('download')}
                        data-testid="calc-sip-download-btn"
                        className="bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-black text-white font-semibold py-6 text-base"
                      >
                        <Download className="mr-2" size={18} />
                        Download PDF
                      </Button>
                      <Button 
                        onClick={() => downloadSIPPDF('share')}
                        data-testid="calc-sip-share-btn"
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-6 text-base"
                      >
                        <MessageCircle className="mr-2" size={18} />
                        Share on WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* SWP Calculator */}
            <TabsContent value="swp">
              <div className="grid lg:grid-cols-2 gap-4 sm:gap-8">
                <Card className="border-2 border-slate-200 shadow-xl">
                  <CardHeader className="bg-gradient-to-br from-purple-900 to-purple-800 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <DollarSign size={28} />
                      SWP Parameters
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-8 space-y-6 sm:space-y-8">
                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Initial Investment</Label>
                        <ValueInput value={swpInitialAmount} onChange={(v) => setSwpInitialAmount(v)} min={100000} max={200000000} prefix="₹" colorCls="text-purple-600" testId="input-swpInitialAmount" />
                      </div>
                      <Slider
                        value={[swpInitialAmount]}
                        onValueChange={(value) => setSwpInitialAmount(value[0])}
                        min={100000}
                        max={200000000}
                        step={100000}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Monthly Withdrawal</Label>
                        <ValueInput value={swpMonthlyWithdrawal} onChange={(v) => setSwpMonthlyWithdrawal(v)} min={1000} max={1000000} prefix="₹" colorCls="text-purple-600" testId="input-swpMonthlyWithdrawal" />
                      </div>
                      <Slider
                        value={[swpMonthlyWithdrawal]}
                        onValueChange={(value) => setSwpMonthlyWithdrawal(value[0])}
                        min={1000}
                        max={1000000}
                        step={1000}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Duration</Label>
                        <ValueInput value={swpDuration} onChange={(v) => setSwpDuration(v)} min={12} max={360} colorCls="text-purple-600" testId="input-swpDuration" />
                      </div>
                      <Slider
                        value={[swpDuration]}
                        onValueChange={(value) => setSwpDuration(value[0])}
                        min={12}
                        max={360}
                        step={12}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Expected Return (p.a.)</Label>
                        <ValueInput value={swpExpectedReturn} onChange={(v) => setSwpExpectedReturn(v)} min={4} max={20} suffix="%" colorCls="text-purple-600" testId="input-swpExpectedReturn" />
                      </div>
                      <Slider
                        value={[swpExpectedReturn]}
                        onValueChange={(value) => setSwpExpectedReturn(value[0])}
                        min={4}
                        max={20}
                        step={0.5}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Inflation Rate (p.a.)</Label>
                        <ValueInput value={swpInflation} onChange={(v) => setSwpInflation(v)} min={0} max={15} suffix="%" colorCls="text-purple-600" testId="input-swpInflation" />
                      </div>
                      <Slider
                        value={[swpInflation]}
                        onValueChange={(value) => setSwpInflation(value[0])}
                        min={0}
                        max={15}
                        step={0.5}
                        className="py-2"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Withdrawals will increase by this % every year to maintain purchasing power. Set to 0% to keep withdrawals flat.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-purple-200 shadow-xl bg-gradient-to-br from-purple-50 to-white">
                  <CardHeader className="bg-gradient-to-br from-purple-900 to-purple-800 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <TrendingUp size={28} />
                      Withdrawal Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8 space-y-6">
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200">
                      <p className="text-sm text-purple-700 font-semibold mb-2">Initial Investment</p>
                      <p className="text-2xl sm:text-3xl font-bold text-purple-900 break-words">{formatCurrency(swpInitialAmount)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
                      <p className="text-sm text-blue-700 font-semibold mb-2">Total Withdrawn</p>
                      <p className="text-2xl sm:text-3xl font-bold text-blue-900 break-words">{formatCurrency(swpResults.totalWithdrawn)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
                      <p className="text-sm text-green-700 font-semibold mb-2">
                        Final Balance {swpResults.ranOutEarly ? '(Corpus depleted)' : ''}
                      </p>
                      <p className="text-2xl sm:text-3xl font-bold text-green-900 break-words">{formatCurrency(swpResults.finalBalance)}</p>
                      {swpInflation > 0 && !swpResults.ranOutEarly && (
                        <p className="text-xs text-green-700 mt-2">
                          ≈ {formatCurrency(swpResults.realFinalBalance)} in today&apos;s value
                        </p>
                      )}
                    </div>

                    {swpInflation > 0 && (
                      <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl p-6 border-2 border-rose-200">
                        <p className="text-sm text-rose-700 font-semibold mb-2">
                          Last Month&apos;s Withdrawal (inflation-adjusted)
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-rose-900 break-words">
                          {formatCurrency(swpResults.finalMonthlyWithdrawal)}
                        </p>
                        <p className="text-xs text-rose-700 mt-2">
                          Started at {formatCurrency(swpMonthlyWithdrawal)} · stepped up {swpInflation}% every year
                        </p>
                      </div>
                    )}

                    <div className="bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl p-8 border-2 border-amber-300">
                      <p className="text-base text-amber-800 font-semibold mb-3">
                        {swpResults.ranOutEarly ? 'Corpus lasted for' : 'Withdrawal Period'}
                      </p>
                      <p className="text-3xl sm:text-5xl font-bold text-amber-900 mb-4 break-words">
                        {Math.floor(swpResults.months / 12)} Years {swpResults.months % 12 > 0 ? `${swpResults.months % 12} Months` : ''}
                      </p>
                      <p className="text-sm text-amber-700">
                        {swpResults.ranOutEarly
                          ? 'Consider increasing corpus, reducing withdrawal, or lowering inflation step-up.'
                          : `Starting income of ${formatCurrency(swpMonthlyWithdrawal)}/month`}
                      </p>
                    </div>
                    
                    {/* Download PDF Button */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Button 
                        onClick={() => downloadSWPPDF('download')}
                        data-testid="calc-swp-download-btn"
                        className="bg-gradient-to-r from-purple-700 to-purple-900 hover:from-purple-800 hover:to-black text-white font-semibold py-6 text-base"
                      >
                        <Download className="mr-2" size={18} />
                        Download PDF
                      </Button>
                      <Button 
                        onClick={() => downloadSWPPDF('share')}
                        data-testid="calc-swp-share-btn"
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-6 text-base"
                      >
                        <MessageCircle className="mr-2" size={18} />
                        Share on WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Arabpati Calculator - ₹100 Crore Goal */}
            <TabsContent value="arabpati">
              <div className="mb-6 text-center bg-gradient-to-r from-amber-100 to-orange-100 p-6 rounded-xl border-2 border-orange-300">
                <h3 className="text-2xl sm:text-3xl font-bold text-orange-900 mb-2">🎯 Become an Arabpati!</h3>
                <p className="text-lg text-orange-800">Calculate your journey to ₹100 Crore (₹1 Arab)</p>
              </div>

              <div className="grid lg:grid-cols-2 gap-4 sm:gap-8">
                <Card className="border-2 border-orange-200 shadow-xl">
                  <CardHeader className="bg-gradient-to-br from-orange-600 to-amber-600 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <Calendar size={28} />
                      Your Investment Journey
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-8 space-y-6 sm:space-y-8">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                          <Label className="text-base font-semibold text-slate-900">Current Age</Label>
                          <ValueInput value={arabpatiCurrentAge} onChange={(v) => setArabpatiCurrentAge(v)} min={0} max={60} colorCls="text-orange-600" testId="input-arabpatiCurrentAge" />
                        </div>
                        <Slider
                          value={[arabpatiCurrentAge]}
                          onValueChange={(value) => setArabpatiCurrentAge(value[0])}
                          min={0}
                          max={60}
                          step={1}
                          className="py-2"
                        />
                      </div>

                      <div>
                        <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                          <Label className="text-base font-semibold text-slate-900">Target Age</Label>
                          <ValueInput value={arabpatiTargetAge} onChange={(v) => setArabpatiTargetAge(v)} min={Math.max(1, arabpatiCurrentAge + 1)} max={75} colorCls="text-orange-600" testId="input-arabpatiTargetAge" />
                        </div>
                        <Slider
                          value={[arabpatiTargetAge]}
                          onValueChange={(value) => setArabpatiTargetAge(value[0])}
                          min={Math.max(1, arabpatiCurrentAge + 1)}
                          max={75}
                          step={1}
                          className="py-2"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Initial Investment</Label>
                        <ValueInput value={arabpatiInitialAmount} onChange={(v) => setArabpatiInitialAmount(v)} min={0} max={100000000} prefix="₹" colorCls="text-orange-600" testId="input-arabpatiInitialAmount" />
                      </div>
                      <Slider
                        value={[arabpatiInitialAmount]}
                        onValueChange={(value) => setArabpatiInitialAmount(value[0])}
                        min={0}
                        max={100000000}
                        step={500000}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Monthly SIP</Label>
                        <ValueInput value={arabpatiMonthlySIP} onChange={(v) => setArabpatiMonthlySIP(v)} min={0} max={10000000} prefix="₹" colorCls="text-orange-600" testId="input-arabpatiMonthlySIP" />
                      </div>
                      <Slider
                        value={[arabpatiMonthlySIP]}
                        onValueChange={(value) => setArabpatiMonthlySIP(value[0])}
                        min={0}
                        max={10000000}
                        step={1000}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Expected Return (p.a.)</Label>
                        <ValueInput value={arabpatiExpectedReturn} onChange={(v) => setArabpatiExpectedReturn(v)} min={8} max={18} suffix="%" colorCls="text-orange-600" testId="input-arabpatiExpectedReturn" />
                      </div>
                      <Slider
                        value={[arabpatiExpectedReturn]}
                        onValueChange={(value) => setArabpatiExpectedReturn(value[0])}
                        min={8}
                        max={18}
                        step={0.5}
                        className="py-2"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-orange-200 shadow-xl bg-gradient-to-br from-orange-50 to-amber-50">
                  <CardHeader className="bg-gradient-to-br from-orange-600 to-amber-600 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <TrendingUp size={28} />
                      Goal Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8 space-y-6">
                    <div className="bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl p-6 border-2 border-orange-300">
                      <p className="text-sm text-orange-700 font-semibold mb-2">Goal Target</p>
                      <p className="text-3xl sm:text-4xl font-bold text-orange-900 break-words">₹100 Crore</p>
                      <p className="text-xs text-orange-600 mt-1">(₹1,00,00,00,000)</p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
                      <p className="text-sm text-blue-700 font-semibold mb-2">Your Projected Corpus</p>
                      <p className="text-2xl sm:text-3xl font-bold text-blue-900 break-words">{formatCurrency(arabpatiResults.futureValue)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
                      <p className="text-sm text-green-700 font-semibold mb-2">Goal Achievement</p>
                      <div className="flex items-end gap-3">
                        <p className="text-3xl sm:text-5xl font-bold text-green-900 break-words">{arabpatiResults.progressPercent.toFixed(1)}%</p>
                        {arabpatiResults.goalAchieved && (
                          <span className="text-2xl mb-2">🎉</span>
                        )}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4 mt-3">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-emerald-600 h-4 rounded-full transition-all duration-500"
                          style={{ width: `${arabpatiResults.progressPercent}%` }}
                        ></div>
                      </div>
                    </div>

                    {arabpatiResults.goalAchieved ? (
                      <div className="bg-gradient-to-br from-emerald-100 to-green-200 rounded-xl p-8 border-2 border-emerald-400">
                        <p className="text-2xl font-bold text-emerald-900 mb-2">🎊 Goal Achieved!</p>
                        <p className="text-base text-emerald-800">
                          Congratulations! You&apos;ll surpass ₹100 Crore in {arabpatiResults.yearsToGo} years!
                        </p>
                        <p className="text-sm text-emerald-700 mt-3">
                          Excess: {formatCurrency(arabpatiResults.futureValue - 1000000000)}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-gradient-to-br from-amber-100 to-orange-200 rounded-xl p-6 border-2 border-amber-400">
                        <p className="text-base text-amber-900 font-semibold mb-2">To Reach ₹100 Crore:</p>
                        <p className="text-sm text-amber-800 mb-3">
                          Shortfall: {formatCurrency(arabpatiResults.shortfall)}
                        </p>
                        <div className="bg-white/50 rounded-lg p-4 mt-3">
                          <p className="text-sm text-amber-900 mb-1">Required Monthly SIP:</p>
                          <p className="text-2xl font-bold text-amber-900">{formatCurrency(arabpatiResults.requiredSIP)}</p>
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-slate-600 bg-slate-100 p-4 rounded-lg">
                      <p className="font-semibold mb-1">Your Investment Summary:</p>
                      <p>• Years to Goal: {arabpatiResults.yearsToGo} years</p>
                      <p>• Total Invested: {formatCurrency(arabpatiResults.totalInvested)}</p>
                      <p>• Expected Gains: {formatCurrency(arabpatiResults.gains)}</p>
                    </div>
                    
                    {/* Download PDF Button */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Button 
                        onClick={() => downloadArabpatiPDF('download')}
                        data-testid="calc-arabpati-download-btn"
                        className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-semibold py-6 text-base"
                      >
                        <Download className="mr-2" size={18} />
                        Download PDF
                      </Button>
                      <Button 
                        onClick={() => downloadArabpatiPDF('share')}
                        data-testid="calc-arabpati-share-btn"
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-6 text-base"
                      >
                        <MessageCircle className="mr-2" size={18} />
                        Share on WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Retirement Calculator */}
            <TabsContent value="retirement">
              <div className="grid lg:grid-cols-2 gap-4 sm:gap-8">
                <Card className="border-2 border-slate-200 shadow-xl">
                  <CardHeader className="bg-gradient-to-br from-blue-900 to-blue-800 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <PiggyBank size={28} />
                      Retirement Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8 space-y-6">
                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base font-semibold text-slate-900">Current Age</Label>
                        <ValueInput value={currentAge} onChange={(v) => setCurrentAge(v)} min={18} max={60} colorCls="text-blue-600" testId="input-currentAge" />
                      </div>
                      <Slider
                        value={[currentAge]}
                        onValueChange={(value) => setCurrentAge(value[0])}
                        min={18}
                        max={60}
                        step={1}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base font-semibold text-slate-900">Retirement Age</Label>
                        <ValueInput value={retirementAge} onChange={(v) => setRetirementAge(v)} min={45} max={75} colorCls="text-blue-600" testId="input-retirementAge" />
                      </div>
                      <Slider
                        value={[retirementAge]}
                        onValueChange={(value) => setRetirementAge(value[0])}
                        min={45}
                        max={75}
                        step={1}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base font-semibold text-slate-900">Current Monthly Expenses</Label>
                        <ValueInput value={monthlyExpenses} onChange={(v) => setMonthlyExpenses(v)} min={10000} max={1000000} prefix="₹" colorCls="text-blue-600" testId="input-monthlyExpenses" />
                      </div>
                      <Slider
                        value={[monthlyExpenses]}
                        onValueChange={(value) => setMonthlyExpenses(value[0])}
                        min={10000}
                        max={1000000}
                        step={5000}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base font-semibold text-slate-900">Expected Inflation</Label>
                        <ValueInput value={inflationRate} onChange={(v) => setInflationRate(v)} min={3} max={10} suffix="%" colorCls="text-blue-600" testId="input-inflationRate" />
                      </div>
                      <Slider
                        value={[inflationRate]}
                        onValueChange={(value) => setInflationRate(value[0])}
                        min={3}
                        max={10}
                        step={0.5}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base font-semibold text-slate-900">Expected Return</Label>
                        <ValueInput value={expectedReturn} onChange={(v) => setExpectedReturn(v)} min={6} max={18} suffix="%" colorCls="text-blue-600" testId="input-expectedReturn" />
                      </div>
                      <Slider
                        value={[expectedReturn]}
                        onValueChange={(value) => setExpectedReturn(value[0])}
                        min={6}
                        max={18}
                        step={0.5}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base font-semibold text-slate-900">Life Expectancy</Label>
                        <ValueInput value={lifeExpectancy} onChange={(v) => setLifeExpectancy(v)} min={65} max={100} colorCls="text-blue-600" testId="input-lifeExpectancy" />
                      </div>
                      <Slider
                        value={[lifeExpectancy]}
                        onValueChange={(value) => setLifeExpectancy(value[0])}
                        min={65}
                        max={100}
                        step={1}
                        className="py-2"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-blue-200 shadow-xl bg-gradient-to-br from-blue-50 to-white">
                  <CardHeader className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <Calculator size={28} />
                      Retirement Plan
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8 space-y-6">
                    <div className="bg-white rounded-xl p-6 border-2 border-slate-100">
                      <p className="text-sm text-slate-600 mb-1">Years to Retirement</p>
                      <p className="text-2xl font-bold text-slate-900">{retirementResults.yearsToRetirement} Years</p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border-2 border-orange-200">
                      <p className="text-sm text-orange-700 font-semibold mb-2">Future Monthly Expenses (at retirement)</p>
                      <p className="text-2xl sm:text-3xl font-bold text-orange-900 break-words">{formatCurrency(retirementResults.futureMonthlyExpenses)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl p-6 border-2 border-purple-300">
                      <p className="text-sm text-purple-800 font-semibold mb-3">Corpus Needed at Retirement</p>
                      <p className="text-3xl sm:text-4xl font-bold text-purple-900 mb-2 break-words">{formatCurrency(retirementResults.corpusNeeded)}</p>
                      <p className="text-xs text-purple-700">
                        To sustain till age {lifeExpectancy}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-xl p-8 border-2 border-green-300">
                      <p className="text-base text-green-800 font-semibold mb-3">Monthly Savings Required</p>
                      <p className="text-3xl sm:text-5xl font-bold text-green-900 mb-4 break-words">{formatCurrency(retirementResults.monthlySavingsRequired)}</p>
                      <p className="text-sm text-green-700">
                        Start saving this amount monthly to achieve your retirement goal
                      </p>
                    </div>
                    
                    {/* Download PDF Button */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Button 
                        onClick={() => downloadRetirementPDF('download')}
                        data-testid="calc-retirement-download-btn"
                        className="bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-black text-white font-semibold py-6 text-base"
                      >
                        <Download className="mr-2" size={18} />
                        Download PDF
                      </Button>
                      <Button 
                        onClick={() => downloadRetirementPDF('share')}
                        data-testid="calc-retirement-share-btn"
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-6 text-base"
                      >
                        <MessageCircle className="mr-2" size={18} />
                        Share on WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* STP Calculator */}
            <TabsContent value="stp">
              <div className="grid lg:grid-cols-2 gap-4 sm:gap-8">
                <Card className="border-2 border-slate-200 shadow-xl">
                  <CardHeader className="bg-gradient-to-br from-purple-900 to-purple-800 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <ArrowRightLeft size={28} />
                      STP Parameters
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8 space-y-6">
                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base font-semibold text-slate-900">Initial Investment</Label>
                        <ValueInput value={stpInitialAmount} onChange={(v) => setStpInitialAmount(v)} min={100000} max={200000000} prefix="₹" colorCls="text-purple-600" testId="input-stpInitialAmount" />
                      </div>
                      <Slider
                        value={[stpInitialAmount]}
                        onValueChange={(value) => setStpInitialAmount(value[0])}
                        min={100000}
                        max={200000000}
                        step={100000}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base font-semibold text-slate-900">Monthly Transfer</Label>
                        <ValueInput value={stpTransferAmount} onChange={(v) => setStpTransferAmount(v)} min={1000} max={1000000} prefix="₹" colorCls="text-purple-600" testId="input-stpTransferAmount" />
                      </div>
                      <Slider
                        value={[stpTransferAmount]}
                        onValueChange={(value) => setStpTransferAmount(value[0])}
                        min={1000}
                        max={1000000}
                        step={1000}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base font-semibold text-slate-900">Transfer Duration</Label>
                        <ValueInput value={stpDuration} onChange={(v) => setStpDuration(v)} min={6} max={60} colorCls="text-purple-600" testId="input-stpDuration" />
                      </div>
                      <Slider
                        value={[stpDuration]}
                        onValueChange={(value) => setStpDuration(value[0])}
                        min={6}
                        max={60}
                        step={1}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base font-semibold text-slate-900">Debt Fund Return (p.a.)</Label>
                        <ValueInput value={stpDebtReturn} onChange={(v) => setStpDebtReturn(v)} min={4} max={12} suffix="%" colorCls="text-purple-600" testId="input-stpDebtReturn" />
                      </div>
                      <Slider
                        value={[stpDebtReturn]}
                        onValueChange={(value) => setStpDebtReturn(value[0])}
                        min={4}
                        max={12}
                        step={0.5}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                        <Label className="text-base font-semibold text-slate-900">Equity Fund Return (p.a.)</Label>
                        <ValueInput value={stpEquityReturn} onChange={(v) => setStpEquityReturn(v)} min={8} max={20} suffix="%" colorCls="text-purple-600" testId="input-stpEquityReturn" />
                      </div>
                      <Slider
                        value={[stpEquityReturn]}
                        onValueChange={(value) => setStpEquityReturn(value[0])}
                        min={8}
                        max={20}
                        step={0.5}
                        className="py-2"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-purple-200 shadow-xl bg-gradient-to-br from-purple-50 to-white">
                  <CardHeader className="bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <TrendingUp size={28} />
                      STP Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl p-4 border-2 border-slate-100">
                        <p className="text-xs text-slate-600 mb-1">Total Transferred</p>
                        <p className="text-lg font-bold text-slate-900">{formatCurrency(stpResults.totalTransferred)}</p>
                      </div>
                      <div className="bg-white rounded-xl p-4 border-2 border-slate-100">
                        <p className="text-xs text-slate-600 mb-1">Duration</p>
                        <p className="text-lg font-bold text-slate-900">{stpDuration} Months</p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
                      <p className="text-sm text-blue-700 font-semibold mb-2">Remaining in Debt Fund</p>
                      <p className="text-2xl sm:text-3xl font-bold text-blue-900 break-words">{formatCurrency(stpResults.debtBalance)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
                      <p className="text-sm text-green-700 font-semibold mb-2">Equity Fund Value</p>
                      <p className="text-2xl sm:text-3xl font-bold text-green-900 break-words">{formatCurrency(stpResults.equityBalance)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl p-6 border-2 border-amber-300">
                      <p className="text-sm text-amber-800 font-semibold mb-2">Total Gains</p>
                      <p className="text-2xl sm:text-3xl font-bold text-amber-900 break-words">{formatCurrency(stpResults.totalGains)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-xl p-6 border-2 border-indigo-300">
                      <p className="text-sm text-indigo-800 font-semibold mb-2">XIRR (Annualized Return)</p>
                      <p className="text-3xl sm:text-4xl font-bold text-indigo-900 mb-2 break-words">{stpResults.xirr.toFixed(2)}% p.a.</p>
                      <p className="text-xs text-indigo-700">Extended Internal Rate of Return</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl p-8 border-2 border-purple-300">
                      <p className="text-base text-purple-800 font-semibold mb-3">Final Corpus</p>
                      <p className="text-3xl sm:text-5xl font-bold text-purple-900 mb-4 break-words">{formatCurrency(stpResults.finalCorpus)}</p>
                      <p className="text-sm text-purple-700">
                        Combined value after {stpDuration} months
                      </p>
                    </div>
                    
                    {/* Download PDF Button */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Button 
                        onClick={() => downloadSTPPDF('download')}
                        data-testid="calc-stp-download-btn"
                        className="bg-gradient-to-r from-purple-700 to-indigo-900 hover:from-purple-800 hover:to-black text-white font-semibold py-6 text-base"
                      >
                        <Download className="mr-2" size={18} />
                        Download PDF
                      </Button>
                      <Button 
                        onClick={() => downloadSTPPDF('share')}
                        data-testid="calc-stp-share-btn"
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-6 text-base"
                      >
                        <MessageCircle className="mr-2" size={18} />
                        Share on WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Child Education Calculator */}
            <TabsContent value="education">
              <div className="grid lg:grid-cols-2 gap-4 sm:gap-8">
                {/* Inputs */}
                <Card className="border-2 border-pink-200 shadow-xl">
                  <CardHeader className="bg-gradient-to-br from-pink-600 to-rose-700 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <BookOpen size={28} />
                      Education Goals
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-8 space-y-6">
                    {/* Common assumptions */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="flex justify-between items-center gap-2 mb-3 flex-wrap">
                          <Label className="text-base font-semibold text-slate-900">Education Inflation</Label>
                          <ValueInput value={childInflation} onChange={(v) => setChildInflation(v)} min={7} max={15} suffix="%" colorCls="text-rose-600" testId="input-childInflation" />
                        </div>
                        <Slider
                          value={[childInflation]}
                          onValueChange={(value) => setChildInflation(value[0])}
                          min={7}
                          max={15}
                          step={0.5}
                          className="py-2"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between items-center gap-2 mb-3 flex-wrap">
                          <Label className="text-base font-semibold text-slate-900">Expected Return</Label>
                          <ValueInput value={childExpectedReturn} onChange={(v) => setChildExpectedReturn(v)} min={6} max={18} suffix="%" colorCls="text-rose-600" testId="input-childExpectedReturn" />
                        </div>
                        <Slider
                          value={[childExpectedReturn]}
                          onValueChange={(value) => setChildExpectedReturn(value[0])}
                          min={6}
                          max={18}
                          step={0.5}
                          className="py-2"
                        />
                      </div>
                    </div>

                    {/* Children list */}
                    {children.map((child, idx) => (
                      <div key={child.id} data-testid={`child-${child.id}-card`} className="border-2 border-pink-100 rounded-xl p-4 sm:p-5 bg-pink-50/40 space-y-4 relative">
                        <div className="flex items-center justify-between gap-2">
                          <Input
                            value={child.name}
                            onChange={(e) => updateChild(child.id, 'name', e.target.value)}
                            className="font-bold text-base sm:text-lg text-pink-800 bg-white border-pink-200 max-w-[60%]"
                          />
                          {children.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeChild(child.id)}
                              data-testid={`remove-child-${child.id}-btn`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 size={16} className="mr-1" />
                              Remove
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <div className="flex justify-between items-center gap-2 mb-2 flex-wrap">
                              <Label className="text-sm font-semibold text-slate-900">Current Age</Label>
                              <span className="text-base sm:text-lg font-bold text-rose-600">{child.currentAge} yrs</span>
                            </div>
                            <Slider
                              value={[child.currentAge]}
                              onValueChange={(v) => updateChild(child.id, 'currentAge', v[0])}
                              min={0}
                              max={Math.max(0, child.educationAge - 1)}
                              step={1}
                              className="py-2"
                            />
                          </div>
                          <div>
                            <div className="flex justify-between items-center gap-2 mb-2 flex-wrap">
                              <Label className="text-sm font-semibold text-slate-900">Education At Age</Label>
                              <span className="text-base sm:text-lg font-bold text-rose-600">{child.educationAge} yrs</span>
                            </div>
                            <Slider
                              value={[child.educationAge]}
                              onValueChange={(v) => updateChild(child.id, 'educationAge', v[0])}
                              min={Math.max(child.currentAge + 1, 5)}
                              max={30}
                              step={1}
                              className="py-2"
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm font-semibold text-slate-900 block mb-2">Education Type</Label>
                          <Select
                            value={child.educationType}
                            onValueChange={(v) => updateChild(child.id, 'educationType', v)}
                          >
                            <SelectTrigger data-testid={`child-${child.id}-edu-type`} className="w-full bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(EDUCATION_PRESETS).map(([key, val]) => (
                                <SelectItem key={key} value={key}>
                                  {val.label}{val.cost ? ` — ${formatCurrency(val.cost)}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {child.educationType === 'others' && (
                          <div>
                            <Label className="text-sm font-semibold text-slate-900 block mb-2">Custom Cost (today&apos;s value)</Label>
                            <Input
                              type="number"
                              min={0}
                              value={child.customCost}
                              onChange={(e) => updateChild(child.id, 'customCost', Number(e.target.value))}
                              data-testid={`child-${child.id}-custom-cost`}
                              className="bg-white"
                              placeholder="e.g. 1500000"
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    <Button
                      onClick={addChild}
                      data-testid="add-child-btn"
                      variant="outline"
                      className="w-full border-2 border-dashed border-pink-400 text-pink-700 hover:bg-pink-50 hover:text-pink-800 py-5"
                    >
                      <Plus size={18} className="mr-2" />
                      Add Another Child
                    </Button>
                  </CardContent>
                </Card>

                {/* Results */}
                <Card className="border-2 border-pink-200 shadow-xl bg-gradient-to-br from-pink-50 to-rose-50">
                  <CardHeader className="bg-gradient-to-br from-rose-600 to-pink-700 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <GraduationCap size={28} />
                      Education Plan Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-8 space-y-5">
                    {educationResults.children.map((c) => (
                      <div key={c.id} className="bg-white rounded-xl p-4 sm:p-5 shadow border border-pink-200">
                        <p className="text-base sm:text-lg font-bold text-pink-800 mb-1">{c.name}</p>
                        <p className="text-xs sm:text-sm text-slate-600 mb-3">
                          {EDUCATION_PRESETS[c.educationType].label} · {c.years} years to go
                        </p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Cost Today</p>
                            <p className="font-bold text-slate-900 break-words">{formatCurrency(c.todayCost)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Future Cost</p>
                            <p className="font-bold text-rose-700 break-words">{formatCurrency(c.futureCost)}</p>
                          </div>
                          <div className="col-span-2 mt-1 pt-3 border-t border-pink-100">
                            <p className="text-xs text-slate-500">Required Monthly SIP</p>
                            <p className="text-2xl sm:text-3xl font-bold text-pink-700 break-words">{formatCurrency(c.monthlySIP)}</p>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Combined totals */}
                    {educationResults.children.length > 1 && (
                      <div className="bg-gradient-to-br from-rose-600 to-pink-700 rounded-xl p-5 sm:p-6 text-white shadow-xl">
                        <p className="text-sm font-semibold opacity-90 mb-2">Total Monthly SIP (All Children)</p>
                        <p className="text-3xl sm:text-4xl font-bold mb-3 break-words">{formatCurrency(educationResults.totalMonthlySIP)}</p>
                        <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                          <div>
                            <p className="opacity-80">Total Investment</p>
                            <p className="font-bold break-words">{formatCurrency(educationResults.totalInvestment)}</p>
                          </div>
                          <div>
                            <p className="opacity-80">Total Future Goal</p>
                            <p className="font-bold break-words">{formatCurrency(educationResults.totalFutureCost)}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* PDF Action Buttons */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                      <Button
                        onClick={() => downloadEducationPDF('download')}
                        data-testid="calc-education-download-btn"
                        className="bg-gradient-to-r from-pink-700 to-rose-800 hover:from-pink-800 hover:to-rose-900 text-white font-semibold py-6 text-base"
                      >
                        <Download className="mr-2" size={18} />
                        Download PDF
                      </Button>
                      <Button
                        onClick={() => downloadEducationPDF('share')}
                        data-testid="calc-education-share-btn"
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-6 text-base"
                      >
                        <MessageCircle className="mr-2" size={18} />
                        Share on WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Goal Planner Calculator */}
            <TabsContent value="goal">
              <div className="grid lg:grid-cols-2 gap-4 sm:gap-8">
                {/* Inputs */}
                <Card className="border-2 border-emerald-200 shadow-xl">
                  <CardHeader className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <Target size={28} />
                      Your Dream Goal
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-8 space-y-5">
                    {/* Preset goal types */}
                    <div>
                      <Label className="text-base sm:text-lg font-semibold text-slate-900 block mb-3">Choose Goal Type</Label>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {Object.entries(GOAL_PRESETS).map(([key, val]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              setGoalType(key);
                              setGoalName(val.label);
                              if (val.cost !== null) setGoalCostToday(val.cost);
                            }}
                            data-testid={`goal-preset-${key}`}
                            className={`p-2 sm:p-3 rounded-xl border-2 transition-all text-center ${
                              goalType === key
                                ? 'border-emerald-500 bg-emerald-50 shadow-md scale-105'
                                : 'border-slate-200 bg-white hover:border-emerald-300'
                            }`}
                          >
                            <div className="text-xl sm:text-2xl">{val.icon}</div>
                            <div className="text-xs sm:text-sm font-semibold text-slate-700 mt-1">{val.label}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-base sm:text-lg font-semibold text-slate-900 block mb-2">Goal Name</Label>
                      <Input
                        value={goalName}
                        onChange={(e) => setGoalName(e.target.value)}
                        data-testid="goal-name-input"
                        className="bg-white border-2 text-base sm:text-lg"
                        placeholder="e.g. Honda City, 2BHK in Salt Lake..."
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Cost Today</Label>
                        <ValueInput value={goalCostToday} onChange={setGoalCostToday} min={0} max={1000000000} prefix="₹" colorCls="text-emerald-600" testId="input-goalCostToday" />
                      </div>
                      <Slider
                        value={[goalCostToday]}
                        onValueChange={(v) => setGoalCostToday(v[0])}
                        min={0}
                        max={100000000}
                        step={10000}
                        className="py-2"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Time to Goal</Label>
                        <ValueInput value={goalYears} onChange={setGoalYears} min={1} max={50} suffix="yrs" colorCls="text-emerald-600" testId="input-goalYears" width="w-20" />
                      </div>
                      <Slider
                        value={[goalYears]}
                        onValueChange={(v) => setGoalYears(v[0])}
                        min={1}
                        max={50}
                        step={1}
                        className="py-2"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="flex justify-between items-center gap-2 mb-2 flex-wrap">
                          <Label className="text-sm sm:text-base font-semibold text-slate-900">Inflation</Label>
                          <ValueInput value={goalInflation} onChange={setGoalInflation} min={0} max={20} suffix="%" colorCls="text-emerald-600" testId="input-goalInflation" width="w-16" />
                        </div>
                        <Slider
                          value={[goalInflation]}
                          onValueChange={(v) => setGoalInflation(v[0])}
                          min={0}
                          max={20}
                          step={0.5}
                          className="py-2"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between items-center gap-2 mb-2 flex-wrap">
                          <Label className="text-sm sm:text-base font-semibold text-slate-900">Return</Label>
                          <ValueInput value={goalReturn} onChange={setGoalReturn} min={0} max={25} suffix="%" colorCls="text-emerald-600" testId="input-goalReturn" width="w-16" />
                        </div>
                        <Slider
                          value={[goalReturn]}
                          onValueChange={(v) => setGoalReturn(v[0])}
                          min={0}
                          max={25}
                          step={0.5}
                          className="py-2"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center gap-2 mb-3 flex-wrap">
                        <Label className="text-base sm:text-lg font-semibold text-slate-900">Existing Savings (optional)</Label>
                        <ValueInput value={goalExistingSavings} onChange={setGoalExistingSavings} min={0} max={1000000000} prefix="₹" colorCls="text-emerald-600" testId="input-goalExistingSavings" />
                      </div>
                      <Slider
                        value={[goalExistingSavings]}
                        onValueChange={(v) => setGoalExistingSavings(v[0])}
                        min={0}
                        max={50000000}
                        step={10000}
                        className="py-2"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Results */}
                <Card className="border-2 border-emerald-200 shadow-xl bg-gradient-to-br from-emerald-50 to-teal-50">
                  <CardHeader className="bg-gradient-to-br from-teal-600 to-emerald-700 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                      <Target size={28} />
                      Plan to Achieve Your Goal
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-8 space-y-5">
                    <div className="bg-white rounded-xl p-5 shadow border border-emerald-200">
                      <p className="text-sm text-slate-500 mb-1">Future Cost of {goalName || GOAL_PRESETS[goalType].label}</p>
                      <p className="text-3xl sm:text-4xl font-bold text-emerald-700 break-words">
                        {formatCurrency(goalResults.futureCost)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        (in {goalYears} year{goalYears > 1 ? 's' : ''}, at {goalInflation}% inflation)
                      </p>
                    </div>

                    {goalExistingSavings > 0 && (
                      <div className="bg-white rounded-xl p-4 shadow border border-emerald-100">
                        <p className="text-sm text-slate-500">Existing Savings (Future Value)</p>
                        <p className="text-xl sm:text-2xl font-bold text-slate-900 break-words">
                          {formatCurrency(goalResults.existingFV)}
                        </p>
                      </div>
                    )}

                    <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl p-5 sm:p-6 text-white shadow-xl">
                      <p className="text-sm font-semibold opacity-90 mb-2">Required Monthly SIP</p>
                      <p className="text-3xl sm:text-5xl font-bold mb-3 break-words">
                        {formatCurrency(goalResults.monthlySIP)}
                      </p>
                      <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                        <div>
                          <p className="opacity-80">Total Investment</p>
                          <p className="font-bold break-words">{formatCurrency(goalResults.totalInvestment)}</p>
                        </div>
                        <div>
                          <p className="opacity-80">Wealth Gain</p>
                          <p className="font-bold break-words">{formatCurrency(goalResults.wealthGain)}</p>
                        </div>
                      </div>
                    </div>

                    {/* PDF Action Buttons */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                      <Button
                        onClick={() => downloadGoalPDF('download')}
                        data-testid="calc-goal-download-btn"
                        className="bg-gradient-to-r from-emerald-700 to-teal-800 hover:from-emerald-800 hover:to-teal-900 text-white font-semibold py-6 text-base"
                      >
                        <Download className="mr-2" size={18} />
                        Download PDF
                      </Button>
                      <Button
                        onClick={() => downloadGoalPDF('share')}
                        data-testid="calc-goal-share-btn"
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-6 text-base"
                      >
                        <MessageCircle className="mr-2" size={18} />
                        Share on WhatsApp
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          {/* Get In Touch CTA - visible on all calculator tabs */}
          <div className="mt-8 sm:mt-10 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 rounded-2xl p-5 sm:p-8 shadow-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6 text-white">
              <div className="text-center md:text-left">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">
                  Loved our calculators?
                </h3>
                <p className="text-sm sm:text-lg text-white/95">
                  Get in touch for more — let our experts turn these projections into a real plan for you.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <Button
                  onClick={() => window.open('https://rubiconfin.investwell.app/app/#/contactUs', '_blank', 'noopener,noreferrer')}
                  size="lg"
                  data-testid="calc-contact-form-cta"
                  className="w-full sm:w-auto bg-white hover:bg-slate-100 text-orange-600 font-bold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg whitespace-nowrap shadow-lg flex items-center justify-center gap-2"
                >
                  <FileText size={20} />
                  Get In Touch
                </Button>
                <Button
                  onClick={() => navigate('/contact')}
                  size="lg"
                  data-testid="calc-contact-details-cta"
                  className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg whitespace-nowrap shadow-lg flex items-center justify-center gap-2 border-2 border-white"
                >
                  <Phone size={20} />
                  Contact Details
                </Button>
              </div>
            </div>
          </div>

          {/* Risk Disclaimer */}
          <RiskDisclaimer />
        </div>
      </div>
    </section>
  );
};

export default InvestmentCalculators;
